import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDT } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const TRANSFER_STATUS_CLS = {
  executed: "bg-signal-green/15 text-signal-green",
  approved: "bg-primary/15 text-primary",
};

export default function TransfersTab() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [bins, setBins] = useState([]);
  const [form, setForm] = useState({ sku: "", from_bin: "", to_bin: "", quantity: "", reason: "" });
  const canCreate = ["admin", "warehouse_manager", "supervisor"].includes(user?.role);
  const canApprove = ["admin", "warehouse_manager"].includes(user?.role);

  const load = () => api.get("/stock/transfers").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/locations").then((r) => setBins(r.data.map((l) => l.bin))).catch(() => {});
  }, []);

  const submit = async () => {
    try {
      await api.post("/stock/transfers", { ...form, quantity: parseInt(form.quantity) || 0 });
      toast.success("Transfer requested");
      setForm({ sku: "", from_bin: "", to_bin: "", quantity: "", reason: "" });
      load();
    } catch (e) { toast.error(String(e.response?.data?.detail || "Failed")); }
  };

  const act = async (id, action) => {
    try {
      await api.post(`/stock/transfers/${id}/${action}`, {});
      toast.success(`Transfer ${action}d`);
      load();
    } catch (e) { toast.error(String(e.response?.data?.detail || "Failed")); }
  };

  return (
    <div className="space-y-4" data-testid="transfers-tab">
      {canCreate && (
        <div className="bg-card border border-border rounded-lg p-4 grid grid-cols-2 md:grid-cols-6 gap-2">
          <Input placeholder="SKU" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} data-testid="trf-sku" />
          <Input list="bin-list" placeholder="From bin" value={form.from_bin} onChange={(e) => setForm({ ...form, from_bin: e.target.value })} data-testid="trf-from" />
          <Input list="bin-list" placeholder="To bin" value={form.to_bin} onChange={(e) => setForm({ ...form, to_bin: e.target.value })} data-testid="trf-to" />
          <datalist id="bin-list">{bins.map((b) => <option key={b} value={b} />)}</datalist>
          <Input type="number" placeholder="Qty" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="trf-qty" />
          <Input placeholder="Reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="trf-reason" />
          <Button onClick={submit} className="bg-primary text-primary-foreground" data-testid="trf-submit">Request Transfer</Button>
        </div>
      )}
      <div className="bg-card border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="transfers-table">
          <thead><tr className="border-b border-border text-left">
            {["ID", "SKU", "From → To", "Qty", "Requested By", "Status", "Approved By", "Executed By", ""].map((h) => (
              <th key={h} className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground whitespace-nowrap">{h}</th>))}
          </tr></thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.transfer_id} className="border-b border-border/50" data-testid={`trf-row-${t.transfer_id}`}>
                <td className="px-4 py-2.5 metric text-xs">{t.transfer_id}</td>
                <td className="px-4 py-2.5 metric font-bold">{t.sku}</td>
                <td className="px-4 py-2.5 metric text-xs">{t.from_bin} → {t.to_bin}</td>
                <td className="px-4 py-2.5 metric">{t.quantity}</td>
                <td className="px-4 py-2.5 text-xs">{t.requested_by}</td>
                <td className="px-4 py-2.5"><span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded ${TRANSFER_STATUS_CLS[t.status] || "bg-signal-amber/15 text-signal-amber"}`}>{t.status}</span></td>
                <td className="px-4 py-2.5 text-xs">{t.approved_by || "—"}</td>
                <td className="px-4 py-2.5 text-xs">{t.executed_by || "—"}</td>
                <td className="px-4 py-2.5 whitespace-nowrap">
                  {t.status === "pending" && canApprove && t.requested_by_id !== user?.user_id && (
                    <Button size="sm" variant="outline" onClick={() => act(t.transfer_id, "approve")} data-testid={`trf-approve-${t.transfer_id}`}>Approve</Button>)}
                  {t.status === "approved" && (
                    <Button size="sm" variant="outline" onClick={() => act(t.transfer_id, "execute")} data-testid={`trf-execute-${t.transfer_id}`}>Execute</Button>)}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">No transfers</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
